"use client";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useProgressRouter as useRouter } from "@/app/_utils/hooks/useProgressRouter";
import {
  IconCircleCheck,
  IconEye,
  IconEyeOff,
  IconLoader2,
  IconLock,
  IconMail,
  IconSend,
} from "@tabler/icons-react";
import Link from "next/link";
import { type PostHog, usePostHog } from "posthog-js/react";
import { useContext, useEffect, useRef, useState, use } from "react";
import { UserContext } from "../../_utils/context/UserDataContext.tsx";

interface ILogInReponseData {
  "error"?: string;
  "idToken": {
    "jwtToken": string;
  };
  "accessToken": {
    "jwtToken": string;
  };
  "refreshToken": {
    "token": string;
  };
}

interface IRequestNotice {
  "icon": React.ReactNode;
  "text": string;
  "variant": "default" | "destructive";
}

interface ILogInSearchParams {
  "verified"?: "true";
  "reset"?: "true";
  "redirect"?: string;
}

export interface IIfOrgExistApiCall {
  exists?: boolean;
  error?: string;
  userId?: string;
}

/**
 * Sets layout and content for user log in page.
 *
 * @param props - The component props containing searchParams for optional query parameters.
 * @returns All elements of the page.
 */
export default function LogIn(
  props: {
    searchParams: Promise<ILogInSearchParams>;
  }
): React.ReactElement {
  const searchParams = use(props.searchParams);
  const posthog: PostHog = usePostHog();
  let initialParams;

  if (searchParams.verified === "true") {
    initialParams = {
      "icon": <IconSend className="h-5 w-5" />,
      "text":
        "We are not done yet. Your account has been placed into a waiting " +
        "list. We will send you an email as soon as it is activated and " +
        "ready to use.",
      "variant": "default" as const,
    };
  } else if (searchParams.reset === "true") {
    initialParams = {
      "icon": <IconCircleCheck className="h-5 w-5" />,
      "text":
        "Your password has been successfully reset! You can now log " +
        "in with your new credentials.",
      "variant": "default" as const,
    };
  }

  const router = useRouter();
  const [requestError, setRequestError] = useState<string>();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const email = useRef<HTMLInputElement>(null);
  const password = useRef<HTMLInputElement>(null);
  const { setUser } = useContext(UserContext);

  const [requestNotice, setRequestNotice] = useState<
    IRequestNotice | undefined
  >(initialParams);

  const saveOrganization = async (): Promise<void> => {
    await fetch("/api/v1/organization/create", {
      "body": JSON.stringify({
        "owner": "%ownerId",
        "ownerName": "%ownerName",
        "ownerEmail": "%ownerEmail",
        "name": "My Organization",
        "dateCreated": Date.now(),
        "subscriptionPlan": "PaymentA",
        "subscriptionExpirationDate": Date.now(),
        "activeUsers": [],
        "members": [],
      }),
      "cache": "no-store",
      "method": "POST",
    })
      .then(async (res) => res.json())
      .catch((err) => {
        console.error(err);
      });
  };

  // Ensure that user drop down widget is never displayed in this page:
  useEffect(() => {
    setUser(null);
  }, [setUser]);

  return (
    <div className="container p-4 mx-auto max-w-[500px]">
      <Card className="w-full">
        <CardHeader className="space-y-1">
          <CardTitle className="text-2xl font-bold text-center">
            Log In
          </CardTitle>
          <CardDescription className="text-center">
            Enter your email and password to access your account
          </CardDescription>
        </CardHeader>
        <CardContent>
          {requestNotice && (
            <Alert variant={requestNotice.variant} className="mb-6">
              <div className="flex items-center gap-3">
                {requestNotice.icon}
                <AlertDescription>{requestNotice.text}</AlertDescription>
              </div>
            </Alert>
          )}

          <form
            className="space-y-4"
            onSubmit={(event): void => {
              event.preventDefault();
              setRequestError(undefined);
              setRequestNotice(undefined);
              setIsLoading(true);

              fetch("/api/auth/log-in", {
                method: "POST",
                cache: "no-store",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  email: email.current?.value ?? "",
                  password: password.current?.value ?? "",
                }),
              })
                .then(async (res) => res.json())
                .then(async (data: ILogInReponseData) => {
                  if (typeof data.error !== "undefined") {
                    setRequestError(data.error);
                    console.error(data.error);
                  } else {
                    await fetch("/api/v1/organization/check-org", {
                      "body": JSON.stringify({
                        "email": email.current?.value,
                      }),
                      "cache": "no-store",
                      "method": "POST",
                    })
                      .then(async (res) => res.json())
                      .then(async (response: IIfOrgExistApiCall) => {
                        // Identify user with PostHog after successful authentication
                        if (response.userId !== undefined) {
                          try {
                            posthog.identify(response.userId);
                          } catch (error) {
                            console.warn(
                              "Failed to identify user with PostHog:",
                              error
                            );
                          }
                        }

                        if (
                          typeof response.error === "string" &&
                          response.error !== ""
                        ) {
                          console.error(
                            "Error checking organization:",
                            response.error
                          );
                          setRequestError(
                            "Unable to verify organization. Please try again later."
                          );

                          return;
                        }

                        if (
                          typeof response.exists === "boolean" &&
                          !response.exists
                        ) {
                          await saveOrganization();
                          router.refresh();
                        } else {
                          if (typeof searchParams.redirect !== "undefined") {
                            router.push(searchParams.redirect);
                          } else {
                            router.push("/");
                          }

                          router.refresh();
                        }
                      });
                  }
                })
                .catch((error) => {
                  console.error(error);
                })
                .finally(() => {
                  setIsLoading(false);
                });
            }}
          >
            <div className="space-y-2">
              <Label
                htmlFor="log-in-email"
                className="text-sm font-medium"
              >
                Email
              </Label>
              <div className="relative">
                <IconMail className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type="email"
                  className="pl-10"
                  id="log-in-email"
                  placeholder="your@email.com"
                  autoComplete="email"
                  ref={email}
                  required
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="space-y-2 mb-4">
              <Label
                htmlFor="log-in-password"
                className="text-sm font-medium"
              >
                Password
              </Label>
              <div className="relative">
                <IconLock className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  type={showPassword ? "text" : "password"}
                  className="pl-10 pr-10"
                  id="log-in-password"
                  placeholder="••••••••"
                  autoComplete="current-password"
                  ref={password}
                  required
                  disabled={isLoading}
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 bg-transparent hover:bg-transparent"
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <IconEyeOff className="h-4 w-4 text-muted-foreground" />
                  ) : (
                    <IconEye className="h-4 w-4 text-muted-foreground" />
                  )}
                </Button>
              </div>
            </div>
            {requestError !== undefined && requestError !== "" && (
              <Alert variant="destructive" className="mb-2">
                <AlertDescription>{requestError}</AlertDescription>
              </Alert>
            )}
            <p className="text-sm text-muted-foreground text-end pb-2">
              <Link
                href="/log-in/forgot-password"
                className="text-primary hover:text-primary/80 hover:underline"
              >
                Forgot your password?
              </Link>
            </p>
            <Button
              className="w-100 w-full mb-2"
              id="submit-button"
              type="submit"
              disabled={isLoading}
            >
              {isLoading && (
                <IconLoader2 className="me-2 h-4 w-4 animate-spin" />
              )}
              <span className={isLoading ? "sr-only" : ""}>
                {isLoading ? "Loading..." : ""}
              </span>
              Log in
            </Button>
            <div className="text-center">
              <p className="text-sm text-muted-foreground">
                Don&apos;t have an account?{" "}
                <Link
                  href="/sign-up"
                  className="text-primary hover:text-primary/80 hover:underline"
                >
                  Sign up.
                </Link>
              </p>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
